import AsyncSelect from '@/components/crud/AsyncSelect'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/ui/page-header'
import { Switch } from '@/components/ui/switch'
import { SortableTH, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import api from '@/lib/api'
import { useSessionState } from '@/lib/useSessionState'
import { formatBRL, formatCnpj, formatDate } from '@/lib/utils'
import {
    Download,
    FilePlus2,
    FileSpreadsheet,
    Plus,
    Search,
    Send,
    X
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BaixarNfsModal from './BaixarNfsModal'
import CaixaModal from './CaixaModal'
import EmailModal from './EmailModal'
import GerarNfModal from './GerarNfModal'
import GerarRascunhoModal from './GerarRascunhoModal'
import ValorDivergente from './ValorDivergente'
import { NF_STATUS_DEFAULT, statusMeta } from './nfStatus'
import { useNfAcoes } from './useNfAcoes'

export default function NfList() {
  const navigate = useNavigate()

  const [items, setItems] = useState([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  // Filtros e ordenação persistem na aba: voltar de /nf/:id reabre a listagem como estava.
  const [sort, setSort] = useSessionState('nf.lista.sort', { key: 'id', direction: 'desc' })

  const [q, setQ] = useSessionState('nf.lista.q', '')
  const [clienteID, setClienteID] = useSessionState('nf.lista.clienteID', '')
  const [clienteLabel, setClienteLabel] = useSessionState('nf.lista.clienteLabel', '')
  const [competencia, setCompetencia] = useSessionState('nf.lista.competencia', '')
  const [todos, setTodos] = useSessionState('nf.lista.todos', false)

  const [rascunhoOpen, setRascunhoOpen] = useState(false)
  const [gerarOpen, setGerarOpen] = useState(false)
  const [baixarNfsOpen, setBaixarNfsOpen] = useState(false)
  const [caixaOpen, setCaixaOpen] = useState(false)
  const [emailNf, setEmailNf] = useState(null)

  const buildParams = useCallback(
    (nextOffset) => ({
      q,
      clienteID,
      competencia,
      status: todos ? '' : NF_STATUS_DEFAULT.join(','),
      sort: sort.key,
      direction: sort.direction,
      limit: 30,
      offset: nextOffset,
    }),
    [q, clienteID, competencia, todos, sort]
  )

  const load = useCallback(
    async (reset) => {
      if (loading) return
      setLoading(true)
      try {
        const nextOffset = reset ? 0 : offset
        const { data } = await api.get('/nfs', { params: buildParams(nextOffset) })
        setItems((prev) => (reset ? data.data : [...prev, ...data.data]))
        setOffset(data.offset)
        setHasMore(data.hasMore)
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildParams, offset, loading]
  )

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0)
      load(true)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, clienteID, competencia, todos, sort])

  function handleSort(key) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  function onScroll(e) {
    const el = e.target
    if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      load(false)
    }
  }

  const hasFilters = q || clienteID || competencia || todos

  function clearFilters() {
    setQ(''); setClienteID(''); setClienteLabel(''); setCompetencia(''); setTodos(false)
  }

  // ---------------- Ações da linha ----------------
  const { acoes, dialogs } = useNfAcoes({
    onDuplicada: () => load(true),
    onStatusAlterado: (nf, novo) =>
      setItems((prev) => prev.map((x) => (x.nfID === nf.nfID ? { ...x, status: novo } : x))),
    onExcluida: () => load(true),
    onEvento: () => load(true),
  })

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Notas Fiscais"
        subtitle="Emissão e acompanhamento das NFS-e."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/nf/novo')}><Plus className="h-4 w-4" /> Novo</Button>
            <Button variant="outline" onClick={() => setBaixarNfsOpen(true)}>
              <Download className="h-4 w-4" /> Baixar NF’s
            </Button>
            <Button variant="outline" onClick={() => setCaixaOpen(true)}>
              <FileSpreadsheet className="h-4 w-4" /> Caixa
            </Button>
            <Button  onClick={() => setRascunhoOpen(true)}>
              <FilePlus2 className="h-4 w-4" /> Gerar Rascunhos
            </Button>
            <Button onClick={() => setGerarOpen(true)} className="bg-chart-2">
              <Send className="h-4 w-4" /> Gerar NF’s
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              name="busca-nf"
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar ID, NF, CNPJ, cliente, contrato, descrição..."
              className="pl-9"
            />
          </div>

          <div className="w-96">
            <AsyncSelect
              endpoint="/clientes"
              value={clienteID}
              onChange={(v, c) => { setClienteID(v); setClienteLabel(c?.nome ?? '') }}
              optionValue="clienteID"
              optionLabel="nome"
              placeholder="Cliente"
              initialLabel={clienteLabel}
            />
          </div>

          <Input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} className="w-auto" title="Mês de competência" />

          <Switch checked={todos} onChange={setTodos} label="Todos" title="Exibir NFs de todos os status" className="ml-1" />

          {hasFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4" /> Limpar
            </Button>
          )}
        </div>
      </PageHeader>

      <div onScroll={onScroll} className="flex-1 overflow-auto p-6 pt-4">
        <Table>
          <THead>
            <tr>
              <SortableTH sortKey="id" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>ID</SortableTH>
              <SortableTH sortKey="emissao" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Emissão</SortableTH>
              <SortableTH sortKey="numero" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>NF</SortableTH>
              <SortableTH sortKey="cliente" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Cliente</SortableTH>
              <SortableTH sortKey="cnpj" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>CNPJ</SortableTH>
              <SortableTH sortKey="contrato" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Contrato</SortableTH>
              <SortableTH sortKey="valor" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort} align="right">Valor</SortableTH>
              <SortableTH sortKey="descricao" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort}>Descrição</SortableTH>
              <SortableTH sortKey="status" activeSortKey={sort.key} direction={sort.direction} onSort={handleSort} align="center">Status</SortableTH>
              <TH className="text-right">Ações</TH>
            </tr>
          </THead>
          <TBody>
            {items.map((nf, i) => {
              const meta = statusMeta(nf.status)
              return (
                <TR key={nf.nfID} index={i} clickable onClick={() => navigate(`/nf/${nf.nfID}`)}>
                  <TD className="text-muted-foreground tabular-nums">{nf.nfID}</TD>
                  <TD>{nf.dataEmissao ? formatDate(nf.dataEmissao) : <span className="text-muted-foreground">—</span>}</TD>
                  <TD>{nf.numero || <span className="text-muted-foreground">—</span>}</TD>
                  <TD className="font-medium">{nf.clienteNome}</TD>
                  <TD className="tabular-nums">{formatCnpj(nf.clienteCnpj) || <span className="text-muted-foreground">-</span>}</TD>
                  <TD>{nf.numeroContrato}</TD>
                  <TD className="text-right">
                    <span className="inline-flex items-center justify-end gap-1">
                      {formatBRL(nf.valor)}
                      <ValorDivergente status={nf.status} valor={nf.valor} valorContratado={nf.valorContratado} />
                    </span>
                  </TD>
                  <TD className="max-w-55 truncate" title={nf.descricaoServico}>{nf.descricaoServico}</TD>
                  <TD className="text-center"><Badge variant={meta.variant}>{meta.label}</Badge></TD>
                  <TD className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-0.5">
                      {acoes(nf).map((a) => (
                        <IconBtn
                          key={a.key}
                          title={a.disabled ? a.motivo : a.label}
                          disabled={a.disabled}
                          danger={a.danger}
                          onClick={a.onClick}
                        >
                          <a.icon className="h-4 w-4" />
                        </IconBtn>
                      ))}
                      {/* <IconBtn title={emitido ? 'Enviar por e-mail' : 'Disponível após emissão'} disabled={!emitido} onClick={() => setEmailNf(nf)}>
                        <span className="relative inline-flex">
                          <MailCheck className="h-4 w-4 relative top-0.5" />
                          {nf.emailsEnviados > 0 && (
                            <span className="absolute -top-1 -right-2 text-[10px] bg-primary text-primary-foreground rounded-full px-1 leading-4 min-w-3.75 text-center">{nf.emailsEnviados}</span>
                          )}
                        </span>
                      </IconBtn> */}
                    </div>
                  </TD>
                </TR>
              )
            })}
            {items.length === 0 && !loading && (
              <tr>
                <TD colSpan={10} className="text-center text-muted-foreground py-10">Nenhuma NF encontrada.</TD>
              </tr>
            )}
          </TBody>
        </Table>
        {loading && <p className="text-center text-sm text-muted-foreground py-4">Carregando...</p>}
      </div>

      <GerarRascunhoModal open={rascunhoOpen} onClose={() => setRascunhoOpen(false)} onDone={() => { setRascunhoOpen(false); load(true) }} />
      <BaixarNfsModal open={baixarNfsOpen} onClose={() => setBaixarNfsOpen(false)} />
      <CaixaModal open={caixaOpen} onClose={() => setCaixaOpen(false)} />
      {/* onDone só recarrega a listagem por trás: o modal permanece aberto na etapa de resultado. */}
      <GerarNfModal open={gerarOpen} onClose={() => setGerarOpen(false)} onDone={() => load(true)} />
      <EmailModal nf={emailNf} onClose={() => setEmailNf(null)} onSent={() => load(true)} />

      {dialogs}
    </div>
  )
}

function IconBtn({ title, onClick, disabled, danger, children }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={
        'p-1.5 rounded-md transition-colors ' +
        (disabled
          ? 'text-muted-foreground/40 cursor-not-allowed'
          : danger
          ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent/60')
      }
    >
      {children}
    </button>
  )
}
